import { useEffect, useState } from "react";
import { Card, Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Header from "../../components/header";
import image from "../../assets/image/home.webp";
import { handleError } from "../../helpers/handleError";
import { deleteAccountApi, getMyEventsApi, updateUserProfileApi } from "../../api/api";
import { LOGIN_SUCCESS, LOGOUT_SUCCESS } from "../../redux/actions/action";
import "./profile.css";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AccountProfile = () => {
  const auth = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", dni: "" });
  const [saving, setSaving] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const parseTimeParts = (value) => {
    const raw = String(value || "").trim();
    const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;
    const h = Number(match[1]);
    const m = Number(match[2]);
    const s = typeof match[3] !== "undefined" ? Number(match[3]) : 0;
    if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59 || s < 0 || s > 59) return null;
    return { h, m, s };
  };

  const computeEventEndAt = (ev) => {
    if (!ev) return null;
    const endDateRaw = ev.endDate || ev.end_date || ev.enddate || ev.eventEndDate || ev.event_end_date || ev.startDate;
    if (!endDateRaw) return null;
    const end = new Date(endDateRaw);
    if (Number.isNaN(end.getTime())) return null;
    const timeRaw = ev.timeTo || ev.time_to || ev.timeToStr || ev.timeFrom || ev.time_from;
    const t = parseTimeParts(timeRaw);
    if (t) end.setHours(t.h, t.m, t.s, 0);
    return end;
  };

  const hasActiveEvents = (events) => {
    if (!Array.isArray(events)) return false;
    const now = Date.now();
    return events.some((ev) => {
      const endAt = computeEventEndAt(ev);
      if (!endAt) return false;
      return endAt.getTime() >= now;
    });
  };

  const nav = [
    { title: "Inicio", path: "/event" },
    { title: "Mi perfil", path: "/account/profile" },
    { title: "Datos personales", path: "/account/profile" },
  ];

  useEffect(() => {
    const { user } = auth || {};
    setForm({
      name: user?.name || "",
      email: user?.email || "",
      dni: user?.dni || "",
    });
  }, [auth?.user?.name, auth?.user?.email, auth?.user?.dni]);

  useEffect(() => {
    const needsBootstrap = !!auth?.accessToken && auth?.isAuthenticated && (!auth?.user || auth?.user?.dni == null || auth?.user?.dni === "");
    if (!needsBootstrap || bootstrapping) return;
    (async () => {
      try {
        setBootstrapping(true);
        const res = await updateUserProfileApi(auth.accessToken, {});
        const updatedUser = res?.data?.info?.user;
        if (updatedUser) {
          dispatch({
            type: LOGIN_SUCCESS,
            payload: {
              accessToken: auth.accessToken,
              refreshToken: auth.refreshToken,
              user: updatedUser,
            },
          });
        }
      } catch (_) {
        // noop: si falla seguimos con los datos actuales
      } finally {
        setBootstrapping(false);
      }
    })();
  }, [auth?.user?.dni, auth?.isAuthenticated, auth?.accessToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      // Sanitizar y validar DNI (7 a 10 digitos) solo si se provee
      const dniClean = String(form.dni || "").replace(/\D+/g, "").slice(0, 10);
      if (dniClean && !/^\d{7,10}$/.test(dniClean)) {
        toast.error("El DNI debe ser numerico (7 a 10 digitos)");
        setSaving(false);
        return;
      }
      const payload = { ...form, dni: dniClean || null };
      const res = await updateUserProfileApi(auth.accessToken, payload);
      if (res?.status === 200) {
        const updatedUser = res?.data?.info?.user || { ...auth.user, ...payload };
        dispatch({
          type: LOGIN_SUCCESS,
          payload: {
            accessToken: auth.accessToken,
            refreshToken: auth.refreshToken,
            user: updatedUser,
          },
        });

        try { toast.success("Listo! Actualizamos tus datos personales."); } catch (_) {}
      }
    } catch (error) {
      handleError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleting) return;
    try {
      setDeleting(true);
      let events = [];
      try {
        const resEvents = await getMyEventsApi(auth.accessToken);
        events = resEvents?.data?.info || resEvents?.data?.data || resEvents?.data || [];
      } catch (_) {}
      if (hasActiveEvents(events)) {
        toast.error("No podés eliminar la cuenta si tenés eventos vigentes.");
        return;
      }
      const confirmDelete = window.confirm("Esta accion es permanente. Queres eliminar tu cuenta?");
      if (!confirmDelete) return;
      const res = await deleteAccountApi(auth.accessToken);
      if (res?.status === 200) {
        dispatch({ type: LOGOUT_SUCCESS });
        try { toast.success("Cuenta eliminada."); } catch (_) {}
        navigate("/event", { replace: true });
        return;
      }
      toast.error(res?.data?.message || "No se pudo eliminar la cuenta.");
    } catch (error) {
      handleError(error);
      toast.error(error?.response?.data?.message || "No se pudo eliminar la cuenta.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnFocusLoss draggable pauseOnHover theme="light" />
        <div className="cardDatosPersonales">
            <div className="profile-title">Datos personales</div>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="name">
                <Form.Label>Usuario</Form.Label>
                <Form.Control
                  className="correo"
                  type="text"
                  placeholder="Nombre y apellido"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  className="correo"
                  type="email"
                  placeholder="tu@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-4" controlId="dni">
                <Form.Label>DNI</Form.Label>
                <Form.Control
                  className="correo"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Tu DNI"
                  value={form.dni}
                  onInput={(e) => { e.target.value = (e.target.value || '').replace(/\D+/g, '').slice(0,10); setForm({ ...form, dni: e.target.value }); }}
                  onChange={(e) => setForm({ ...form, dni: (e.target.value || '').replace(/\D+/g, '').slice(0,10) })}
                />
              </Form.Group>

              <div className="profile-actions">
                <Button type="submit" className="success" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar cambios"}
                </Button>
                <Button type="button" className="danger" onClick={handleDeleteAccount} disabled={deleting}>
                  {deleting ? "Eliminando..." : "Eliminar cuenta"}
                </Button>

              </div>
              <div className="profile-back" onClick={() => navigate('/event')}>
                <span className="profile-back-arrow">‹</span> Volver a <span className="profile-back-link">Eventos</span>
              </div>
            </Form>
            </div>
    </>
  );
};

export default AccountProfile;
